// @ts-strict-ignore
import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, Illustration, toast } from '@node-space/ui-components'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { RequestError } from 'components/Errors/RequestError'
import { useDeleteContactInstrumentMutation } from 'hooks/mutations/useDeleteContactInstrumentMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ContactInstrumentProps, InstrumentType } from 'types/contacts'
import { contactAtom, contactInstrumentAtom } from '../atoms/contactAtoms'

export interface ContactInstrumentDeleteModalProps {
  onModalClose: () => void
}

export const ContactInstrumentDeleteModal = ({
  onModalClose,
}: ContactInstrumentDeleteModalProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const contact = useAtomValue(contactAtom)
  const contactInstrument = useAtomValue(contactInstrumentAtom)
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { mutate: deleteContactInstrument, isPending: isDeletingContactInstrument } =
    useDeleteContactInstrumentMutation()

  const handleDeleteContactInstrument = () => {
    const payload: ContactInstrumentProps = {
      contactId: contact?.id,
      instrumentId: contactInstrument?.id,
    }
    resetRequestError()
    deleteContactInstrument(payload, {
      onSuccess: () => {
        toast({
          header: t('contacts.deleteContactInstrumentSuccess', {
            description: contactInstrument?.description,
          }),
          variant: 'success',
        })
        queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACT_INSTRUMENTS] })
        onModalClose?.()
      },
      onError: (error: BaseErrorResponse) => {
        const sentryTraceId = logSentryErrorAndGetTraceId('deleteContactInstrument', error)
        setRequestError({
          handledMessage: error?.message,
          errorCode: error?.status,
          sentryTraceId,
          show: true,
        })
      },
    })
  }

  const title =
    contactInstrument?.type === InstrumentType.BANK_ACCOUNT
      ? t('contacts.deleteBankAccount')
      : t('contacts.deleteAddress')

  const description =
    contactInstrument?.type === InstrumentType.BANK_ACCOUNT
      ? t('contacts.deleteBankAccountDescription')
      : t('contacts.deleteAddressDescription')

  return (
    <Dialog
      open
      onClose={onModalClose}
      title={title}
      Illustration={<Illustration name="CloudLightning" />}
      ActionButtons={[
        <Button
          key="deleteKey"
          variant="destructive"
          testId="delete-contact-instrument"
          onClick={handleDeleteContactInstrument}
          loading={isDeletingContactInstrument}
        >
          {t('contacts.delete')}
        </Button>,
        <Button key="cancel" onClick={onModalClose} variant="secondary" testId="close-button">
          {t('cancel')}
        </Button>,
      ]}
    >
      <>
        <p className="text-foreground-prominent text-body-regular text-center">{description}</p>
        {requestError?.show && <RequestError>{requestError?.handledMessage}</RequestError>}
      </>
    </Dialog>
  )
}
